import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Tag } from "lucide-react";

export default function SubscriptionsScreen() {
  const [subs, setSubs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ code: "", discount_percent: 10, usage_limit: 100, expires_in_days: 30 });

  const load = async () => {
    try {
      const [{ data: s }, { data: c }] = await Promise.all([
        adminApi.get("/subscriptions"),
        adminApi.get("/promo-codes"),
      ]);
      setSubs(s.subscriptions || []);
      setCodes(c.codes || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const createCode = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/promo-codes", form);
      toast.success("Promo code created");
      setShowAdd(false);
      setForm({ code: "", discount_percent: 10, usage_limit: 100, expires_in_days: 30 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;
    try {
      await adminApi.delete(`/promo-codes/${code}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-shadow-green mb-1">Subscriptions</h1>
      <p className="text-muted-soft text-sm mb-6">Active premium & platinum users and promo code management.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="glass-card p-5">
          <p className="text-[10px] uppercase tracking-wider text-muted-soft">Active subscriptions</p>
          <p className="text-2xl font-extrabold mt-1">{subs.length}</p>
        </div>
        <div className="glass-card p-5">
          <p className="text-[10px] uppercase tracking-wider text-muted-soft">Premium</p>
          <p className="text-2xl font-extrabold mt-1 text-[#FF8F00]">{subs.filter((s) => s.tier === "premium").length}</p>
        </div>
        <div className="glass-card p-5">
          <p className="text-[10px] uppercase tracking-wider text-muted-soft">Platinum</p>
          <p className="text-2xl font-extrabold mt-1" style={{ color: "#d49bff" }}>{subs.filter((s) => s.tier === "platinum").length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <p className="text-sm font-semibold">Active subscribers</p>
            <span className="text-xs text-muted-soft">{subs.length}</span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {subs.length === 0 ? (
              <p className="p-8 text-center text-soft text-sm">No active subscribers yet.</p>
            ) : subs.map((u) => (
              <div key={u.user_id} className="flex items-center gap-3 px-4 py-3 border-b border-white/3 last:border-b-0" data-testid={`sub-${u.user_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{u.name}</p>
                  <p className="text-[11px] text-muted-soft truncate">{u.email}</p>
                </div>
                <span className={`glass-pill ${u.tier === "platinum" ? "purple" : "amber"}`}>{u.tier}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <p className="text-sm font-semibold">Promo codes</p>
            <button onClick={() => setShowAdd(true)} className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="create-code">
              <Plus size={12} /> New
            </button>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {codes.length === 0 ? (
              <p className="p-8 text-center text-soft text-sm">No promo codes.</p>
            ) : codes.map((c) => (
              <div key={c.code} className="flex items-center gap-3 px-4 py-3 border-b border-white/3 last:border-b-0" data-testid={`code-${c.code}`}>
                <span className="glass-pill"><Tag size={11} /> {c.code}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold">{c.discount_percent}% off</p>
                  <p className="text-[11px] text-muted-soft">{c.used_count}/{c.usage_limit} used · expires {new Date(c.expires_at).toLocaleDateString()}</p>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Copied"); }} className="text-white/40 hover:text-white p-1">
                  <Copy size={14} />
                </button>
                <button onClick={() => deleteCode(c.code)} className="text-white/40 hover:text-red-400 p-1">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={createCode} className="glass-card-strong p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">New promo code</h2>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-soft">Code</label>
                <input className="glass-input mt-1" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="SPRING25" required data-testid="code-input" />
              </div>
              <div>
                <label className="text-xs text-muted-soft">Discount %</label>
                <input type="number" min={1} max={100} className="glass-input mt-1" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: +e.target.value })} required />
              </div>
              <div>
                <label className="text-xs text-muted-soft">Usage limit</label>
                <input type="number" min={1} className="glass-input mt-1" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: +e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-soft">Expires in (days)</label>
                <input type="number" min={1} className="glass-input mt-1" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: +e.target.value })} />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-outline flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="submit-code">Create</button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
